import { Invoice } from 'types'
import { INVOICE_STATUS, InvoiceStatus } from '../constants'

export const applyIdSearch = (
  invoices: Invoice[],
  searchId: string
): Invoice[] => {
  if (!searchId) return invoices
  return invoices.filter((inv) => String(inv.id).includes(searchId))
}

export const applyCustomerSearch = (
  invoices: Invoice[],
  searchCustomer: string
): Invoice[] => {
  if (!searchCustomer) return invoices
  const q = searchCustomer.toLowerCase()
  return invoices.filter((inv) => {
    const name =
      `${inv.customer?.first_name ?? ''} ${inv.customer?.last_name ?? ''}`.toLowerCase()
    return name.includes(q)
  })
}

export const applyAddressSearch = (
  invoices: Invoice[],
  searchAddress: string
): Invoice[] => {
  if (!searchAddress) return invoices
  const q = searchAddress.toLowerCase()
  return invoices.filter((inv) => {
    const address = (inv.customer?.address ?? '').toLowerCase()
    const city = (inv.customer?.city ?? '').toLowerCase()
    const zip = (inv.customer?.zip_code ?? '').toLowerCase()
    return address.includes(q) || city.includes(q) || zip.includes(q)
  })
}

// "unpaid" is not a real API field; it is derived from finalized=true AND paid=false.
const STATUS_PREDICATES: Record<InvoiceStatus, (inv: Invoice) => boolean> = {
  [INVOICE_STATUS.DRAFT]: (inv) => !inv.finalized,
  [INVOICE_STATUS.FINALIZED]: (inv) => !!inv.finalized,
  [INVOICE_STATUS.PAID]: (inv) => !!inv.paid,
  [INVOICE_STATUS.UNPAID]: (inv) => !!inv.finalized && !inv.paid,
}

const isInvoiceStatus = (status: string): status is InvoiceStatus =>
  Object.values(INVOICE_STATUS).includes(status as InvoiceStatus)

export const applyStatusFilter = (
  invoices: Invoice[],
  statuses: string[]
): Invoice[] => {
  if (!statuses.length) return invoices
  return invoices.filter((inv) =>
    statuses.some(
      (status) => isInvoiceStatus(status) && STATUS_PREDICATES[status](inv)
    )
  )
}

// Deadline ranges are sent to the server via buildDateConditions and never applied client-side.
// This function exists for unit-test coverage of the predicate logic in isolation.
export const applyDeadlineFilter = (
  invoices: Invoice[],
  deadlineFrom?: string,
  deadlineTo?: string
): Invoice[] => {
  if (!deadlineFrom && !deadlineTo) return invoices
  return invoices.filter((inv) => {
    if (!inv.deadline) return false
    if (deadlineFrom && inv.deadline < deadlineFrom) return false
    if (deadlineTo && inv.deadline > deadlineTo) return false
    return true
  })
}
